import { api } from 'shared-web';
import type { QueryParams } from '../types.ts';

// Flow types (based on existing flow data structure)
interface FlowData {
  id: string;
  title: string;
  description: string;
  author: string;
  category: string;
  price: number;
  rating: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  tags: string[];
  featured: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  downloads: number;
  views: number;
  likes: number;
}

interface FlowQueryParams extends QueryParams {
  difficulty?: string;
  minPrice?: number;
  maxPrice?: number;
  featured?: boolean;
  search?: string;
}

export const flowService = {
  // Get paginated flows with filtering (from cached JSON)
  list: async (params?: FlowQueryParams) => {
    const response = await api.get<any>('/v1/public/gallery/flows.json');

    // The cached endpoint returns data directly, not wrapped in ApiResponse format
    const data = response.data || response;

    // Client-side filtering since it's cached data
    let filteredItems = data.items || [];

    if (params?.search || params?.q) {
      const searchTerm = (params?.search || params?.q || '').toLowerCase();
      filteredItems = filteredItems.filter((item: any) =>
        item.title?.toLowerCase().includes(searchTerm) ||
        item.description?.toLowerCase().includes(searchTerm) ||
        item.tags?.some((tag: string) => tag.toLowerCase().includes(searchTerm))
      );
    }

    if (params?.category && params.category !== 'All Flows') {
      filteredItems = filteredItems.filter((item: any) => item.category === params.category);
    }

    if (params?.difficulty) {
      filteredItems = filteredItems.filter((item: any) => item.difficulty === params.difficulty);
    }

    if (params?.minPrice !== undefined) {
      filteredItems = filteredItems.filter((item: any) => (item.price || 0) >= params.minPrice!);
    }

    if (params?.maxPrice !== undefined) {
      filteredItems = filteredItems.filter((item: any) => (item.price || 0) <= params.maxPrice!);
    }

    if (params?.featured) {
      filteredItems = filteredItems.filter((item: any) => item.featured);
    }

    // Client-side pagination
    const page = params?.page || 1;
    const limit = params?.limit || 12;
    const start = (page - 1) * limit;
    const paginatedItems = filteredItems.slice(start, start + limit);

    return {
      items: paginatedItems,
      total: filteredItems.length,
      page,
      limit,
      totalPages: Math.ceil(filteredItems.length / limit)
    };
  },

  // Get single flow by ID (from cached data)
  getById: async (id: string) => {
    const response = await api.get<any>('/v1/public/gallery/flows.json');
    const data = response.data || response;
    const flow = data.items?.find((item: any) => item.id === id);

    if (!flow) {
      throw new Error(`Flow with id ${id} not found`);
    }

    return flow;
  },

  // Use a flow (requires authentication and credits per run)
  use: async (id: string, inputData?: any) => {
    throw new Error('Using flows requires authentication. Please log in to the dashboard.');
  },

  // Get flow usage cost
  getCost: async (id: string) => {
    const response = await api.get<any>('/v1/public/gallery/flows.json');
    const data = response.data || response;
    const flow = data.items?.find((item: any) => item.id === id);

    if (!flow) {
      throw new Error(`Flow with id ${id} not found`);
    }

    return {
      creditsPerRun: flow.price || 0,
      currency: 'credits'
    };
  },

  // Get flow categories (from cached data)
  getCategories: async () => {
    const response = await api.get<any>('/v1/public/gallery/flows.json');
    const data = response.data || response;

    // Extract unique categories from cached flows
    const categories = new Set<string>();
    data.items?.forEach((item: any) => {
      if (item.category) {
        categories.add(item.category);
      }
    });

    return Array.from(categories).sort();
  },

  // Get featured flows (from cached data)
  getFeatured: async (limit?: number) => {
    const response = await api.get<any>('/v1/public/gallery/flows.json');
    const data = response.data || response;

    // Filter featured flows
    let featured = data.items?.filter((item: any) => item.featured) || [];

    // Apply limit if provided
    if (limit && limit > 0) {
      featured = featured.slice(0, limit);
    }

    return featured;
  },

  // Like/unlike a flow (requires authentication)
  toggleLike: async (id: string) => {
    throw new Error('Liking flows requires authentication. Please log in to the dashboard.');
  },
};